import React, { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { useAppStore } from '@/store';
import Home from '@/components/Home';
import MattersPage from '@/components/MattersPage';
import Settings from '@/components/settings/Settings';
import PromptAdmin from '@/components/admin/PromptAdmin';
import TopBar from '@/components/layout/TopBar';
import AppFooter from '@/components/layout/AppFooter';
import Stage0 from '@/components/stages/Stage0';
import Stage1 from '@/components/stages/Stage1';
import Stage2 from '@/components/stages/Stage2';
// Stage25 removed from navigation — the Strategy tab now lives inside
// Stage 2. Backend stage-id stays as 'stage_2_5' (no data change).
import Stage3 from '@/components/stages/Stage3';
import Stage4 from '@/components/stages/Stage4';
import Login from '@/components/auth/Login';
import DiagnosticPage from '@/components/admin/DiagnosticPage';
import UsersPage from '@/components/admin/UsersPage';
import SkillStudio from '@/components/admin/SkillStudio';
import StageJobsPage from '@/components/admin/StageJobsPage';
// ARCH-F01 (Phase 24 3-A) — React error boundary. Wraps the matter
// workbench root so an unhandled exception inside one stage
// no longer crashes the whole app to a blank screen.
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getMe, logout as apiLogout, setUnauthenticatedHandler, postEvent } from '@/api/client';

function MatterView({ onOpenSettings, onOpenPrompts, onOpenSkill, onOpenDiagnostic, onOpenUsers, onOpenStageJobs, onOpenMatters, onLogout, user }) {
  const { activeStage } = useAppStore();

  return (
    <div data-testid="matter-view">
      <TopBar
        onOpenSettings={onOpenSettings}
        onOpenPrompts={onOpenPrompts}
        onOpenSkill={onOpenSkill}
        onOpenDiagnostic={onOpenDiagnostic}
        onOpenUsers={onOpenUsers}
        onOpenStageJobs={onOpenStageJobs}
        onOpenMatters={onOpenMatters}
        onLogout={onLogout}
        currentUser={user}
      />
      {/* Stage 0 — pre-analysis upload screen. New matters land here
          from POST /matter; existing stage_1+ matters skip it on
          open but the tab is still clickable (TopBar gates only on
          STAGE_ORDER index ≤ current_stage index, and stage_0 is
          index 0). */}
      {activeStage === 'stage_0' && <Stage0 />}
      {activeStage === 'stage_1' && <Stage1 />}
      {/* Stage 2 + Strategy tab share one screen. activeStage=stage_2_5
          lands on the Strategy tab inside Stage 2 — backend identifiers
          are unchanged; this is a pure UI merge. */}
      {(activeStage === 'stage_2' || activeStage === 'stage_2_5') && <Stage2 />}
      {activeStage === 'stage_3' && <Stage3 />}
      {activeStage === 'stage_4' && <Stage4 />}
      {/* Shared footer — copyright line + epi compliance disclosure.
          Mounted here so every workspace stage renders the disclosure;
          Home / admin pages keep their own AppFooter mounts (same
          component, same disclosure). */}
      <AppFooter />
    </div>
  );
}

function App() {
  const { matterId, matterState } = useAppStore();
  const [view, setView] = useState('matter');
  const [authState, setAuthState] = useState({ status: 'loading', user: null });
  // Deep links (#/spec/<id>, #/drawings/<id>, …) belong to the Spec and
  // Drawing modules, which are not part of the claims build. Strip any
  // stale hash from an old bookmark so the URL bar stays honest.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;
    try {
      window.history.replaceState(
        null, '', window.location.pathname + window.location.search,
      );
    } catch (_e) { /* private-mode browsers throw; non-fatal */ }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const me = await getMe();
      if (me.authenticated) {
        setAuthState({ status: 'authenticated', user: me.user });
      } else {
        setAuthState({ status: 'anonymous', user: null });
      }
    } catch {
      setAuthState({ status: 'anonymous', user: null });
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    // Any 401 from the API bounces to the login screen — handles
    // mid-session expiry and admin-initiated deactivation cleanly.
    setUnauthenticatedHandler(() => {
      setAuthState({ status: 'anonymous', user: null });
    });
  }, []);

  // page_view beacon — fires every time the top-level view changes
  // UX-F03 (Phase 28 3-H) — bounce non-admin users away from
  // admin-gated views. Was previously a series of inline
  // ``setView('matter')`` calls inside the render conditional
  // (lines ~178/185/192/201/209/224/245/269). Calling setState
  // during render is a React anti-pattern: it causes an
  // immediate second render (a one-frame null flash) and will
  // throw under React 18 strict mode + Concurrent Mode. Doing
  // it inside an effect lets React commit the bounce cleanly.
  //
  // The render-side guards now return null without firing
  // setView — this effect handles the redirect on the next
  // commit cycle. Net behaviour identical for the drafter; no
  // more strict-mode warning + no more wasted render pass.
  useEffect(() => {
    const isAdmin = authState.user?.role === 'admin';
    const adminGated = new Set([
      'settings', 'prompts', 'skill', 'diagnostic',
      'users', 'stage-jobs',
    ]);
    if (adminGated.has(view) && !isAdmin) {
      setView('matter');
    }
  }, [view, authState.user?.role]);

  // while the user is authenticated. Covers matter / matters list /
  // settings / prompts / diagnostic / users. Gated on authenticated
  // so the login screen doesn't produce spurious events, and on the
  // initial load it still fires once we flip to 'authenticated'.
  useEffect(() => {
    if (authState.status !== 'authenticated') return;
    const pageMap = {
      matter: '/matter',
      matters: '/matters',
      settings: '/settings',
      prompts: '/admin/prompts',
      diagnostic: '/admin/diagnostic',
      users: '/admin/users',
      'stage-jobs': '/admin/stage-jobs',
    };
    postEvent({
      event_type: 'page_view',
      event_category: 'navigation',
      element_id: `view_${view}`,
      event_data: { view, path: pageMap[view] || `/${view}` },
    });
  }, [view, authState.status]);

  const handleLogout = useCallback(async () => {
    try { await apiLogout(); } catch { /* best-effort */ }
    // Reset in-memory matter state so another user doesn't see prior data.
    useAppStore.getState().reset?.();
    setAuthState({ status: 'anonymous', user: null });
  }, []);

  if (authState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (authState.status === 'anonymous') {
    return <Login onAuthenticated={refreshAuth} />;
  }

  // Settings, Prompts, Users, Diagnostic are all admin-only. Server
  // enforces via require_admin; the client-side bounce is a UX nicety
  // so a drafter who types the URL doesn't see a 403 shell.
  if (view === 'settings') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <Settings onBack={() => setView('matter')} />;
  }
  if (view === 'prompts') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <PromptAdmin onBack={() => setView('matter')} />;
  }
  if (view === 'skill') {
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <SkillStudio onBack={() => setView('matter')} />;
  }
  if (view === 'diagnostic') {
    // /admin/diagnostic — admin-only inspector. Server enforces the
    // role check; this client-side guard is just a UX nicety.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <DiagnosticPage onBack={() => setView('matter')} />;
  }
  if (view === 'users') {
    // /admin/users — same admin gate as diagnostic.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return (
      <UsersPage
        currentUser={authState.user}
        onBack={() => setView('matter')}
      />
    );
  }
  if (view === 'stage-jobs') {
    // /admin/stage-jobs — cross-module async-job observability.
    // Server-side require_admin enforces; client-side bounce
    // is the same UX nicety as the other admin views.
    if (authState.user?.role !== 'admin') {
      // UX-F03 (Phase 28 3-H) — bounce handled by the
      // setView effect above on next commit; render null
      // here to avoid showing the gated view for a frame.
      return null;
    }
    return <StageJobsPage onBack={() => setView('matter')} />;
  }
  if (view === 'matters') {
    // /matters — available to every authenticated user. Leaving the
    // current matter (if any) intact so "Back" returns to the workflow.
    return (
      <MattersPage onBack={() => setView('matter')} />
    );
  }
  const isAdmin = authState.user?.role === 'admin';
  // Every admin entry point is gated on isAdmin so non-admin users
  // never see the nav button — not greyed out, not CSS-hidden, the
  // prop is undefined and TopBar/Home render guards skip the element
  // entirely (absent from the DOM).
  const onOpenSettings = isAdmin ? () => setView('settings') : undefined;
  const onOpenPrompts = isAdmin ? () => setView('prompts') : undefined;
  const onOpenSkill = isAdmin ? () => setView('skill') : undefined;
  const onOpenDiagnostic = isAdmin ? () => setView('diagnostic') : undefined;
  const onOpenUsers = isAdmin ? () => setView('users') : undefined;
  const onOpenStageJobs = isAdmin ? () => setView('stage-jobs') : undefined;
  // Matters list is available to every authenticated user — not
  // admin-gated. Server still scopes results by workspace.
  const onOpenMatters = () => setView('matters');

  if (matterId && matterState) {
    return (
      <ErrorBoundary label="MatterView">
        <MatterView
          onOpenSettings={onOpenSettings}
          onOpenPrompts={onOpenPrompts}
          onOpenSkill={onOpenSkill}
          onOpenDiagnostic={onOpenDiagnostic}
          onOpenUsers={onOpenUsers}
          onOpenStageJobs={onOpenStageJobs}
          onOpenMatters={onOpenMatters}
          onLogout={handleLogout}
          user={authState.user}
        />
      </ErrorBoundary>
    );
  }

  return (
    <Home
      onOpenSettings={onOpenSettings}
      onOpenPrompts={onOpenPrompts}
      onOpenSkill={onOpenSkill}
      onOpenDiagnostic={onOpenDiagnostic}
      onOpenUsers={onOpenUsers}
      onOpenStageJobs={onOpenStageJobs}
      onOpenMatters={onOpenMatters}
      onLogout={handleLogout}
      currentUser={authState.user}
    />
  );
}

export default App;
